/**
 * WaveSelect — a row of segmented buttons for an EnumParameter.
 *
 * Used for waveform selection and any other small enum choice. Each option
 * renders as a hard-edged button; the active one glows red. Keyboard-navigable
 * via Left/Right arrows when focused.
 */

import type { EnumParameter } from '@ac-slayer/synth-core';

export interface WaveSelectOptions<T extends string> {
  readonly param: EnumParameter<T>;
  readonly label?: string;
}

export class WaveSelect<T extends string> {
  readonly element: HTMLElement;
  private readonly param: EnumParameter<T>;
  private readonly buttons = new Map<T, HTMLButtonElement>();
  private readonly unsubscribe: () => void;

  constructor(opts: WaveSelectOptions<T>) {
    this.param = opts.param;

    this.element = document.createElement('div');
    this.element.className = 'wave-select';

    const label = document.createElement('div');
    label.className = 'label';
    label.textContent = opts.label ?? this.param.name;
    this.element.appendChild(label);

    const group = document.createElement('div');
    group.className = 'wave-select-group';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', opts.label ?? this.param.name);
    this.element.appendChild(group);

    for (const option of this.param.options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wave-select-btn';
      btn.textContent = this.param.formatter(option);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.setAttribute('data-value', option);
      btn.addEventListener('click', () => this.param.set(option, 'ui'));
      btn.addEventListener('keydown', this.onKeyDown);
      this.buttons.set(option, btn);
      group.appendChild(btn);
    }

    this.unsubscribe = this.param.subscribe(() => this.render());
    this.render();
  }

  dispose(): void {
    this.unsubscribe();
  }

  private render(): void {
    for (const [value, btn] of this.buttons) {
      const active = value === this.param.value;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
    }
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const current = this.param.index;
    const next = (current + dir + this.param.options.length) % this.param.options.length;
    this.param.setByIndex(next, 'ui');
    this.buttons.get(this.param.value)?.focus();
  };
}
